import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listMySpots, deleteMySpot } from "@/lib/camping.functions";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

export const Route = createFileRoute("/_authenticated/konto/mina-tips")({
  component: MySpotsPage,
});

function MySpotsPage() {
  const fn = useServerFn(listMySpots);
  const del = useServerFn(deleteMySpot);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["my-spots"], queryFn: () => fn() });

  async function remove(id: string) {
    if (!confirm("Ta bort detta tips?")) return;
    try {
      await del({ data: { id } });
      qc.invalidateQueries({ queryKey: ["my-spots"] });
      toast.success("Borttaget");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte ta bort");
    }
  }

  if (isLoading) return <div className="rounded-3xl border border-border bg-card p-8">Laddar…</div>;
  const list = data?.spots ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl">Mina campingtips</h2>
        <Link to="/campingtips/ny" className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
          <Plus size={14} /> Nytt tips
        </Link>
      </div>

      {list.length === 0 ? (
        <div className="rounded-3xl border border-border bg-card p-8 text-sm text-muted-foreground">
          Du har inte lagt upp några tips än. <Link to="/campingtips/ny" className="text-accent hover:underline">Skapa ditt första</Link>.
        </div>
      ) : (
        <ul className="grid gap-4">
          {list.map((s) => (
            <SpotRow key={s.id} spot={s} onDelete={() => remove(s.id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SpotRow({ spot, onDelete }: { spot: any; onDelete: () => void }) {
  const [img, setImg] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      const first = spot.image_urls?.[0];
      if (!first) return;
      if (first.startsWith("http")) { setImg(first); return; }
      const { data } = await supabase.storage.from("user-content").createSignedUrl(first, 60 * 60);
      setImg(data?.signedUrl ?? null);
    })();
  }, [spot]);
  return (
    <li className="flex items-center gap-4 rounded-3xl border border-border bg-card p-4 elev-shadow">
      <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-2xl bg-secondary">
        {img ? <img src={img} alt="" className="h-full w-full object-cover" /> : <span className="text-xs text-muted-foreground">Ingen bild</span>}
      </div>
      <div className="min-w-0 flex-1">
        <Link to="/campingtips/$slug" params={{ slug: spot.slug }} className="font-display text-lg hover:text-accent">
          {spot.title}
        </Link>
        <p className="text-xs text-muted-foreground">
          {spot.region || "—"} · {spot.spot_type || "camping"} · {new Date(spot.created_at).toLocaleDateString("sv-SE")}
        </p>
      </div>
      <button onClick={onDelete} className="rounded-full border border-border p-2 text-muted-foreground hover:border-destructive hover:text-destructive">
        <Trash2 size={15} />
      </button>
    </li>
  );
}
